// @flow
import { useQuery } from 'react-query';

import ResidentService from '../../../services/residentService';
import useIntl from '../../../hooks/useIntl';
import messages from './messages';

/** A stay of the household at this property (the Move-In correction stays endpoint). */
export type UnitHistoryStay = {
  stayLeaseId: string,
  unitId: string,
  unitLabel: string,
  moveInDate: string,
  moveOutDate: ?string,
  isCurrent: boolean,
  startedBy: 'MOVE_IN' | 'TRANSFER',
  originUnitLabel: ?string,
  endedBy: ?('TRANSFER' | 'MOVE_OUT'),
  nextUnitLabel: ?string,
  isFirstStay: boolean,
  propertyMoveInDate: ?string,
  /** Why the stay's Move-In can't be corrected, whatever the date. */
  blocked: ?{ code: string, message: string },
};

export const UNIT_HISTORY_QUERY_KEY = 'moveInDateCorrectionStays';

/** The household's stays, fetched on every mount (no cache). */
export function useUnitHistoryStays({
  organizationId,
  propertyId,
  residentId,
}: {
  organizationId: string,
  propertyId: string,
  residentId: string,
}) {
  const query = useQuery(
    [UNIT_HISTORY_QUERY_KEY, organizationId, propertyId, residentId],
    ({ signal }) =>
      new ResidentService().getMoveInDateCorrectionStays({
        organizationId,
        propertyId,
        residentId,
        signal,
      }),
    { retry: false, refetchOnWindowFocus: false, cacheTime: 0 },
  );
  const stays: Array<UnitHistoryStay> = query.data?.stays ?? [];
  return {
    stays,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  };
}

/** Row labels and actions of the Unit History table. */
export function useUnitHistoryRows(
  onEditStay: (stay: UnitHistoryStay) => void,
) {
  const intl = useIntl();

  const getStartedLabel = (stay: UnitHistoryStay): string => {
    if (stay.startedBy === 'TRANSFER') {
      return stay.originUnitLabel
        ? intl.formatMessage(messages.transferFrom, {
            unitLabel: stay.originUnitLabel,
          })
        : intl.formatMessage(messages.transfer);
    }
    return intl.formatMessage(
      stay.isFirstStay ? messages.firstMoveIn : messages.movedIn,
    );
  };

  const getEndedLabel = (stay: UnitHistoryStay): string => {
    if (stay.endedBy === 'TRANSFER') {
      return stay.nextUnitLabel
        ? intl.formatMessage(messages.transferTo, {
            unitLabel: stay.nextUnitLabel,
          })
        : intl.formatMessage(messages.transfer);
    }
    if (stay.endedBy === 'MOVE_OUT') {
      return intl.formatMessage(messages.movedOut);
    }
    return intl.formatMessage(messages.current);
  };

  const getEditLabel = (stay: UnitHistoryStay): string =>
    intl.formatMessage(messages.editMoveIn, { unitLabel: stay.unitLabel });

  // Blocked pencils are natively disabled, so this only runs for editable stays.
  const handleEditStay = (stay: UnitHistoryStay) => () => onEditStay(stay);

  return { getStartedLabel, getEndedLabel, getEditLabel, handleEditStay };
}
